import { useContext, useEffect, useRef, useState } from "react";
import { cn, showToast } from "utils/helpers";

import Dropzone from "react-dropzone";
import { Tooltip } from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import { FileCard } from "../FileCard";
import { useField } from "formik";
import { FileCardSelect } from "../FileCard/FileCardSelect";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { reorderList } from "utils/reorderList";
import LabelForm from "../LabelForm/LabelForm";
import { RfxContext } from "newui/features/Rfx/context/RfxContext";
import {
	addProgressToUploading,
	removeProgressFromUploading,
} from "utils/rfxLargeFileUploadHelper";
import { fileTypeChecker, regexType } from "utils/fileTypeChecker";
import { FileUploadDropzone } from "./FileUPloadDropzone";

export const FileUploadSelectTemplate = ({
	label,
	multiple = false,
	children,
	className,
	hint,
	description,
	required,
	types = [],
	maxFileSize,
	defaultFileName,
	selectOptions,
	selectPlaceholder,
	selectName,
	subtitle,
	customMessage,
	rfxTemplateId,
	...props
}) => {
	const [field, meta, helpers] = useField(props);
	const [, /**/ selectMeta] = useField(selectName);
	// Initialize files state directly from Formik's initial value (or empty array)
	const [files, setFiles] = useState(() =>
		meta.value && Array.isArray(meta.value) ? meta.value : []
	);
	// Initialize documentIds from initial files
	const [documentIds, setDocumentIds] = useState(() =>
		meta.value && Array.isArray(meta.value)
			? meta.value
				.filter((file) => file.id && !file.isRemove)
				.map((file) => file.id)
			: []
	);
	const { value } = field;
	const selectedCategory = selectMeta.value ?? selectOptions?.[0]?.value;
	const { setUploadInProgresses } = useContext(RfxContext);

	const inputRef = useRef(null);

	// useEffect to update Formik state when internal 'files' state changes
	useEffect(() => {
		if (files) {
			// separate non-removed and removed files
			const filesSorted = files
				.filter((file) => !file.isRemove)
				.map((file, i) => ({ ...file, sort_order: i + 1 }));
			const filesRemoved = files.filter((file) => file.isRemove);

			const newValue = [...filesSorted, ...filesRemoved];
			const currentValueStr = JSON.stringify(field.value || []); // Compare with Formik value
			const newValueStr = JSON.stringify(newValue);

			// Set formik value only if it has changed
			if (currentValueStr !== newValueStr) {
				helpers.setValue(newValue);
			}
		}
	}, [files, helpers]);

	useEffect(() => {
		// if internal files are empty BUT formik has initial value, sync it.
		if (files.length === 0 && meta.value && meta.value.length > 0) {
			setFiles(meta.value);
			const initialIds = meta.value
				.filter((f) => f.id && !f.isRemove)
				.map((f) => f.id);
			setDocumentIds(initialIds);
		}
	}, [meta.value]);

	const dropHandler = (acceptedFiles, rejectedFiles) => {
		if (!selectedCategory) {
			showToast("Please select a category", "Error");
			return;
		}
		if (rejectedFiles.length > 0) {
			rejectedFiles.forEach((file) => {
				file?.errors?.length > 0 &&
					file.errors[0].code === "file-invalid-type" &&
					showToast("Invalid file type", "Error");
			});
		}
		if (multiple) {
			acceptedFiles.forEach((file, i) => {
				const documentsObj = {
					id: file?.id || null,
					category_id: selectedCategory || 1,
					sort_order: files ? i + 1 + files.length : 1,
					isRemove: 0,
					document: file,
				};
				if (file.size > maxFileSize * 1024 * 1024) {
					showToast(`Maximum file size is ${maxFileSize}MB.`, "Error");
				} else {
					setFiles((prev) => [...(prev || []), documentsObj]);
				}
			});
		} else {
			if (acceptedFiles[0].size > maxFileSize * 1024 * 1024) {
				showToast(`Maximum file size is ${maxFileSize}MB.`, "Error");
			} else {
				setFiles([
					{
						id: acceptedFiles[0]?.id || null,
						category_id: selectedCategory || 1,
						sort_order: 1,
						isRemove: 0,
						document: acceptedFiles[0],
					},
				]);
			}
		}
	};

	const url = (value) => (typeof value === "string" ? value : "");
	const fileExt = (url) =>
		url && typeof url === "string" ? url?.match(regexType) : null;

	const deleteHandler = (index) => {
		const fileToDelete = files[index];

		// Update documentIds if needed
		if (fileToDelete?.id) {
			setDocumentIds((prev) => prev.filter((id) => id !== fileToDelete.id));
		}

		// Actually remove the file from files state
		setFiles((prev) => {
			const newFiles = [...prev];
			newFiles[index] = { ...newFiles[index], isRemove: 1 };
			return newFiles;
		});
	};

	const handleKeyDown = (event) => {
		if (event.key === "Enter") {
			if (inputRef.current) {
				inputRef.current.click();
			}
		}
	};

	const handleFileUploadComplete = (uploadedFile) => {
		if (uploadedFile?.id) {
			// no duplicate IDs
			if (!documentIds.includes(uploadedFile.id)) {
				setDocumentIds((prev) => [...prev, uploadedFile.id]);
			}
		}

		// Update files state using map for better immutability
		setFiles((prevFiles) => {
			if (!prevFiles) return [];

			// Map to create the new state array
			const updatedFiles = prevFiles.map((file) => {
				// Determine the current file's name for comparison
				const currentFileName =
					file?.document instanceof File
						? file?.document?.name?.trim().replace(/\s+/g, "_")
						: file?.document?.path?.trim().replace(/\s+/g, "_");

				// If this is the file that just finished uploading, update it
				if (currentFileName === uploadedFile.fileName) {
					return {
						...file, // Keep existing data
						category_id: uploadedFile.categoryId,
						document: uploadedFile.fileName, // Replace File with string name
						file_size: uploadedFile.fileSize,
						id: uploadedFile.id, // Set/Update the ID from the server
						isRemove: 0,
						path: uploadedFile.documentPath,
					};
				} else {
					// Otherwise, return the file unchanged
					return file;
				}
			});
			return updatedFiles; // Return the new array for the state update
		});

		setUploadInProgresses((prev) => removeProgressFromUploading(prev, true));
	};

	return (
		<div className={cn("w-full", className)}>
			{label && (
				<LabelForm
					label={label}
					required={required}
				>
					{hint && (
						<Tooltip
							dataTip={`input-${props.name}`}
							content={hint}
						>
							<Icon
								icon="info"
								className="text-gray-500"
								size="sm"
							/>
						</Tooltip>
					)}
				</LabelForm>
			)}
			{subtitle && (
				<p className="-mt-1 mb-4 text-sm text-tertiary-600">{subtitle}</p>
			)}
			{description && <p className="text-gray-text mb-2">{description}</p>}

			<div className="cursor-pointer">
				<Dropzone
					onDrop={(acceptedFiles, rejectedFiles) =>
						dropHandler(acceptedFiles, rejectedFiles)
					}
					accept={fileTypeChecker(types)}
					multiple={multiple}
				>
					{({
						getRootProps,
						getInputProps,
						isDragAccept,
						isDragReject,
						isDragActive,
					}) => (
						<FileUploadDropzone
							getRootProps={getRootProps}
							getInputProps={getInputProps}
							isDragAccept={isDragAccept}
							isDragReject={isDragReject}
							isDragActive={isDragActive}
							selectedCategory={selectedCategory}
							meta={meta}
							inputRef={inputRef}
							customMessage={customMessage}
							types={types}
							maxFileSize={maxFileSize}
							handleKeyDown={handleKeyDown}
							testId={props?.testId}
						>
							{children}
						</FileUploadDropzone>
					)}
				</Dropzone>
			</div>
			{typeof value === "string" && fileExt(url(value)) && !multiple ? (
				<>
					{
						<FileCard
							file={{ name: `${defaultFileName}${fileExt[0]}` }}
							setFiles={() => helpers.setValue("")}
						/>
					}
				</>
			) : (
				<DragDropContext
					onDragEnd={(result) => reorderList(files, result, setFiles)}
				>
					<Droppable
						droppableId="questioner-container"
						direction="vertical"
					>
						{(provided) => (
							<div
								id="questioner-container"
								className="my-8"
								{...provided.droppableProps}
								ref={provided.innerRef}
							>
								{files &&
									files.length > 0 &&
									files.map((file, i) => {
										if (!file.isRemove) {
											return (
												<Draggable
													key={`file${i}`}
													draggableId={`file${i}`}
													index={i}
												>
													{(provided, snapshot) => (
														<div
															ref={provided.innerRef}
															{...provided.draggableProps}
															{...provided.dragHandleProps}
															className="py-1"
														>
															<FileCardSelect
																uploadProgress={0}
																className="my-1"
																file={file.document ? file.document : file}
																deleteHandler={() => deleteHandler(i)}
																fileSize={file.file_size || null}
																selectOptions={selectOptions}
																selectValue={Number(file?.category_id)}
																setFiles={setFiles}
																index={i}
																sort_id={file.sort_order}
																draggableProps={provided.dragHandleProps}
																fileExtraData={{
																	category_id: file?.category_id,
																	sort_order: i + 1,
																	model:
																		"rfq_document_documentation_file_template",
																	rfxTemplateId: rfxTemplateId,
																}}
																handleFileUploadComplete={
																	handleFileUploadComplete
																}
																handleFileUploadStart={() => {
																	setUploadInProgresses((prev) =>
																		addProgressToUploading(prev, true)
																	);
																}}
															/>
														</div>
													)}
												</Draggable>
											);
										} else return null;
									})}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			)}

			{meta.error && meta.touched && (
				<p className="my-2 -mt-5 font-roboto text-sm !text-utility-error-500">
					{meta.error}
				</p>
			)}
		</div>
	);
};
